import JSZip from "jszip";
import { pdfjs } from "react-pdf";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url,
).toString();

export const ASSIST_DOC_ACCEPT = ".pdf,.doc,.docx,application/pdf";
export const ASSIST_DOC_MAX_BYTES = 12 * 1024 * 1024;
export const ASSIST_DOC_TEXT_LIMIT = 28000;

const ALLOWED_EXT = new Set(["pdf", "doc", "docx"]);

function fileExtension(file) {
  const name = String(file?.name || "");
  const i = name.lastIndexOf(".");
  return i >= 0 ? name.slice(i + 1).toLowerCase() : "";
}

/** Quick check before attaching a file in the composer (no extract yet). */
export function validateAssistDocumentFile(file) {
  if (!file) throw new Error("No file selected.");
  const extension = fileExtension(file);
  if (!ALLOWED_EXT.has(extension)) {
    throw new Error("Only PDF and Word files (.pdf, .doc, .docx) are allowed.");
  }
  if ((Number(file.size) || 0) > ASSIST_DOC_MAX_BYTES) {
    throw new Error("File is too large. Maximum size is 12 MB.");
  }
  return { extension, fileName: String(file.name || `document.${extension}`) };
}

function decodeXmlEntities(text) {
  return String(text || "")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&#(\d+);/g, (_, n) => String.fromCharCode(Number(n)))
    .replace(/&#x([0-9a-fA-F]+);/g, (_, h) =>
      String.fromCharCode(parseInt(h, 16)),
    );
}

function normalizeExtractedText(raw) {
  return String(raw || "")
    .replace(/\r\n/g, "\n")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .replace(/[ \t]{2,}/g, " ")
    .trim();
}

/** Group PDF text items into visual lines (keeps table-ish columns closer). */
function groupPdfItemsIntoLines(items) {
  const rows = [];
  const sorted = [...(items || [])].sort((a, b) => {
    const ay = Number(a?.transform?.[5] ?? 0);
    const by = Number(b?.transform?.[5] ?? 0);
    if (Math.abs(by - ay) > 2) return by - ay;
    const ax = Number(a?.transform?.[4] ?? 0);
    const bx = Number(b?.transform?.[4] ?? 0);
    return ax - bx;
  });

  let currentY = null;
  let current = [];
  const flush = () => {
    if (!current.length) return;
    const text = current
      .map((it) => String(it?.str || ""))
      .join(" ")
      .replace(/\s+/g, " ")
      .trim();
    if (text) rows.push(text);
    current = [];
  };

  for (const item of sorted) {
    const y = Number(item?.transform?.[5] ?? 0);
    if (currentY == null || Math.abs(y - currentY) <= 3.5) {
      current.push(item);
      currentY = currentY == null ? y : (currentY + y) / 2;
    } else {
      flush();
      current.push(item);
      currentY = y;
    }
  }
  flush();
  return rows;
}

async function extractPdfText(file) {
  const data = await file.arrayBuffer();
  const pdf = await pdfjs.getDocument({ data }).promise;
  const pages = [];
  const maxPages = Math.min(pdf.numPages || 0, 40);
  for (let i = 1; i <= maxPages; i += 1) {
    const page = await pdf.getPage(i);
    const content = await page.getTextContent();
    const lines = groupPdfItemsIntoLines(content.items || []);
    if (lines.length) {
      pages.push(`[Page ${i}]`);
      pages.push(...lines);
      pages.push("");
    }
  }
  return normalizeExtractedText(pages.join("\n"));
}

async function extractDocxText(file) {
  const buffer = await file.arrayBuffer();
  const zip = await JSZip.loadAsync(buffer);
  const documentXml = await zip.file("word/document.xml")?.async("string");
  if (!documentXml) {
    throw new Error("Could not read this Word document.");
  }

  const cellText = (xmlChunk) => {
    const bits = [];
    const re = /<w:t[^>]*>([^<]*)<\/w:t>/gi;
    let m;
    while ((m = re.exec(xmlChunk)) != null) {
      bits.push(decodeXmlEntities(m[1]));
    }
    return bits.join("").replace(/\s+/g, " ").trim();
  };

  const lines = [];
  let tableIndex = 0;

  // Walk document in order: replace each table with structured rows, keep other paragraphs.
  const blockRe = /<w:tbl\b[\s\S]*?<\/w:tbl>|<w:p\b[\s\S]*?<\/w:p>/gi;
  let blockMatch;
  while ((blockMatch = blockRe.exec(documentXml)) != null) {
    const block = blockMatch[0];
    if (/^<w:tbl\b/i.test(block)) {
      tableIndex += 1;
      lines.push(`[Table ${tableIndex}]`);
      const rowRe = /<w:tr\b[\s\S]*?<\/w:tr>/gi;
      let rowMatch;
      while ((rowMatch = rowRe.exec(block)) != null) {
        const cells = [];
        const tcRe = /<w:tc\b[\s\S]*?<\/w:tc>/gi;
        let tcMatch;
        while ((tcMatch = tcRe.exec(rowMatch[0])) != null) {
          const t = cellText(tcMatch[0]);
          if (t) cells.push(t);
        }
        if (cells.length) lines.push(cells.join(" | "));
      }
      lines.push("");
      continue;
    }
    // Skip paragraphs nested inside tables (already handled via tbl match).
    if (/<w:tc[\s>]/i.test(block)) continue;
    const t = cellText(block);
    if (t.length >= 2) lines.push(t);
  }

  const aliases = [];
  const aliasRe = /w:alias="([^"]+)"/gi;
  let match;
  while ((match = aliasRe.exec(documentXml)) != null) {
    const a = decodeXmlEntities(match[1]).trim();
    if (a) aliases.push(a);
  }

  let body = normalizeExtractedText(lines.join("\n"));
  if (aliases.length) {
    body = normalizeExtractedText(
      `Form labels:\n${[...new Set(aliases)].join("\n")}\n\nDocument text:\n${body}`,
    );
  }
  return body;
}

/** Best-effort text scrape from legacy .doc (binary). Prefer .docx when possible. */
async function extractDocLegacyText(file) {
  const buffer = await file.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  let ascii = "";
  for (let i = 0; i < bytes.length; i += 1) {
    const b = bytes[i];
    if (b === 9 || b === 10 || b === 13 || (b >= 32 && b <= 126)) {
      ascii += String.fromCharCode(b);
    } else {
      ascii += " ";
    }
  }
  const cleaned = normalizeExtractedText(
    ascii
      .replace(/[^\x09\x0A\x0D\x20-\x7E]+/g, " ")
      .replace(/(.)\1{8,}/g, "$1"),
  );
  if (cleaned.length < 40) {
    throw new Error(
      "Could not read this .doc file. Please save it as .docx or PDF and try again.",
    );
  }
  return cleaned;
}

/**
 * Extract plain text from an Assist upload (PDF / DOC / DOCX).
 * @returns {Promise<{ fileName: string, extension: string, text: string, clipped: boolean }>}
 */
export async function extractAssistDocumentText(file) {
  if (!file) throw new Error("No file selected.");
  const extension = fileExtension(file);
  if (!ALLOWED_EXT.has(extension)) {
    throw new Error("Only PDF and Word files (.pdf, .doc, .docx) are allowed.");
  }
  if ((Number(file.size) || 0) > ASSIST_DOC_MAX_BYTES) {
    throw new Error("File is too large. Maximum size is 12 MB.");
  }

  let text = "";
  if (extension === "pdf") text = await extractPdfText(file);
  else if (extension === "docx") text = await extractDocxText(file);
  else text = await extractDocLegacyText(file);

  if (!text || text.length < 12) {
    throw new Error(
      "No usable text was found in this file. Try a clearer PDF or Word document.",
    );
  }

  const clipped = text.length > ASSIST_DOC_TEXT_LIMIT;
  return {
    fileName: String(file.name || `document.${extension}`),
    extension,
    text: clipped ? text.slice(0, ASSIST_DOC_TEXT_LIMIT) : text,
    clipped,
  };
}

/**
 * Build the Assist prompt used to generate a form from document text.
 */
export function buildFormFromDocumentPrompt({ fileName, text, clipped, userNote }) {
  const name = String(fileName || "document").trim();
  const note = String(userNote || "").trim();
  return [
    `Build an e-Source form from this uploaded document (“${name}”).`,
    "Map the document structure into fields: section headers, Yes/No questions, checkbox lists, Initials/Date columns, sign/date blocks, and notes.",
    "Preserve table rows as separate questions where possible. Mark identifiers, dates and sign-off as required; keep notes optional.",
    "Do not invent volunteer or NHS identifiers. Stay in British English.",
    note ? `Extra instructions from the user: ${note}` : null,
    clipped
      ? "Note: the document text was truncated for length; use what is provided."
      : null,
    "",
    "Document content:",
    text,
  ]
    .filter(Boolean)
    .join("\n");
}
